import { useEffect, useRef, useState } from "react";
import { Button, Confirm, ErrorNote, Select, Panel } from "../../components/ui";
import { PastDateField } from "../../components/fields";
import { SaveError, SaveStatus } from "../../components/save";
import { useSaveState } from "../../lib/saving";
import {
  listVehicleDocs, uploadVehicleDoc, deleteVehicleDoc, openVehicleDoc,
  shortShareLink, shareLink, formatSize, DOC_KINDS, KIND_LABEL,
  type VehicleDocument, type VehicleDocKind,
} from "./vehicleDocuments";

/**
 * The car's papers: Carfax, window sticker, auction report.
 *
 * Separate from the deal's filed copies, which are the signed contract and
 * cannot be deleted. These can be replaced whenever a fresher one is pulled.
 */
export default function VehicleDocs({ vehicleId }: { vehicleId: string }) {
  const [docs, setDocs] = useState<VehicleDocument[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  /* Upload and remove report themselves: "Saving…", then "Document added ✓
     3:04 PM" once it is filed, or why not. */
  const docSave = useSaveState();
  const [docLabel, setDocLabel] = useState("Saved");
  const [docHint, setDocHint] = useState<string | undefined>(undefined);
  const busy = docSave.saving || sharing;
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState<VehicleDocKind>("carfax");
  /* Today, because a report is usually uploaded the day it is pulled. The
     hint says to change it when that is not true. */
  const today = () => new Date().toISOString().slice(0, 10);
  const [reportedOn, setReportedOn] = useState(today);
  const [shared, setShared] = useState<string | null>(null);
  const [confirmDoc, setConfirmDoc] = useState<VehicleDocument | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    listVehicleDocs(vehicleId)
      .then((rows) => { if (live) setDocs(rows); })
      .catch((e) => setErr(e instanceof Error ? e.message : "Could not read the documents."));
    return () => { live = false; };
  }, [vehicleId]);

  async function onFile(file: File | undefined) {
    if (!file) return;
    setErr(null);
    setDocLabel(`${KIND_LABEL[kind] ?? "Document"} added`);
    setDocHint("Nothing was added. Choose the PDF again to retry.");
    await docSave.run(async () => {
      const added = await uploadVehicleDoc(
        vehicleId, file, kind, reportedOn || null);
      setDocs((d) => [added, ...d]);
      setAdding(false);
      setReportedOn(today());
    }, { timeoutMs: 120_000 });
    if (fileInput.current) fileInput.current.value = "";
  }

  /** A seven-day link, straight onto the clipboard. */
  async function share(doc: VehicleDocument) {
    setSharing(true); setErr(null);
    try {
      let url: string;
      try { url = await shortShareLink(doc); }
      catch { url = await shareLink(doc); }   // no worker: fall back to the long one
      await navigator.clipboard?.writeText(url);
      setShared(doc.id);
      setTimeout(() => setShared(null), 2200);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not make a link.");
    } finally {
      setSharing(false);
    }
  }

  async function open(doc: VehicleDocument) {
    setErr(null);
    try { await openVehicleDoc(doc); }
    catch (e) { setErr(e instanceof Error ? e.message : "Could not open it."); }
  }

  async function remove(doc: VehicleDocument) {
    setErr(null);
    setDocLabel(`${KIND_LABEL[doc.kind] ?? "Document"} removed`);
    setDocHint("The document is still attached.");
    const ok = await docSave.run(() => deleteVehicleDoc(doc));
    setConfirmDoc(null);
    if (ok) setDocs((rows) => rows.filter((r) => r.id !== doc.id));
  }

  const carfax = docs.find((d) => d.kind === "carfax");

  return (
    <Panel title="Documents" rail="record"
           note={carfax ? "Carfax on file" : docs.length ? `${docs.length}` : "no Carfax"}
           foot={!adding && (
             <Button variant="ghost" onClick={() => { setAdding(true); setErr(null); }}>
               Add a document
             </Button>
           )}>
      {docs.length === 0 ? (
        <p className="hint">
          Nothing attached. A Carfax goes here so it stays with the car rather
          than in an email.
        </p>
      ) : (
        <div className="item">
          {docs.map((d) => (
            <div className="li" key={d.id}>
              <span>
                {KIND_LABEL[d.kind] ?? d.kind}
                {d.reported_on ? ` — pulled ${d.reported_on}` : ""}
                <button className="linkbtn tiny" onClick={() => open(d)}>open</button>
                <button className="linkbtn tiny" disabled={busy}
                        onClick={() => void share(d)}>
                  {shared === d.id ? "link copied" : "copy link"}
                </button>
                <button className="linkbtn tiny" disabled={busy}
                        onClick={() => setConfirmDoc(d)}>remove</button>
                <br />
                <span className="dim">{d.filename}</span>
              </span>
              <span className="money">{formatSize(d.byte_size)}</span>
            </div>
          ))}
        </div>
      )}

      {adding && (
        <>
          <div className="grid2">
            <Select label="What is it" value={kind}
                    onChange={(v) => setKind(v as VehicleDocKind)}>
              {DOC_KINDS.map((k) => (
                <option key={k.value} value={k.value}>{k.label}</option>
              ))}
            </Select>
            <PastDateField label="Pulled on" value={reportedOn}
                   onChange={setReportedOn}
                   hint="Change it if the report was pulled earlier" />
          </div>
          <input ref={fileInput} type="file" accept="application/pdf,.pdf"
                 style={{ display: "none" }}
                 onChange={(e) => onFile(e.target.files?.[0])} />
          <div className="actions">
            <Button onClick={() => fileInput.current?.click()} disabled={busy}>
              {docSave.saving ? "Uploading…" : "Choose PDF"}
            </Button>
            <Button variant="ghost" disabled={busy}
                    onClick={() => { setAdding(false); setErr(null); }}>
              Cancel
            </Button>
          </div>
          <p className="hint dim">PDF only, up to 25 MB.</p>
        </>
      )}

      {confirmDoc && (
        <Confirm
          message={`Remove ${KIND_LABEL[confirmDoc.kind] ?? "this document"} `
                 + `(${confirmDoc.filename})? This cannot be undone.`}
          confirmLabel={busy ? "Removing" : "Remove document"}
          onConfirm={() => remove(confirmDoc)}
          onCancel={() => setConfirmDoc(null)}
        />
      )}
      {shared && (
        <p className="hint dim">
          Link copied. Anyone with it can open the report without signing in,
          until you remove the document.
        </p>
      )}
      {err && <ErrorNote>{err}</ErrorNote>}
      <p className="savenote">
        <SaveStatus save={docSave} hideWhenIdle savedLabel={docLabel} />
      </p>
      <SaveError save={docSave} hint={docHint} />
    </Panel>
  );
}
